import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  DollarSign,
  Edit2,
  PackageCheck,
  Plus,
  Trash2,
  Truck,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import { listProducts, listWarehouses } from "@/lib/endpoints";
import {
  confirmPurchaseOrder,
  createPurchaseOrder,
  createSupplier,
  listPayableInvoices,
  listPurchaseOrders,
  listSuppliers,
  payPayableInvoice,
  receivePurchaseOrder,
  updateSupplier,
  type PayableInvoicePublic,
  type PayablePaymentCreate,
  type PurchaseOrderCreate,
  type SupplierCreate,
  type SupplierPublic,
  type SupplierUpdate,
} from "@/lib/purchasing";
import { getSession } from "@/lib/session";

export function PurchasingScreen() {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  const [activeTab, setActiveTab] = useState<"orders" | "suppliers" | "payables">("orders");

  // Proveedores state
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierPublic | null>(null);
  const [supplierForm, setSupplierForm] = useState<SupplierCreate>({
    name: "",
    tax_id: "",
    contact_name: "",
    phone: "",
    email: "",
    address: "",
    credit_days: 0,
    notes: "",
  });

  // Orden de compra state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [receivingOrderId, setReceivingOrderId] = useState<string | null>(null);
  const [receiveInvoiceNumber, setReceiveInvoiceNumber] = useState("");
  const [receiveNotes, setReceiveNotes] = useState("");

  const [orderForm, setOrderForm] = useState<{
    supplier_id: string;
    warehouse_id: string;
    expected_delivery_date: string;
    notes: string;
    lines: Array<{
      variant_id: string;
      name: string;
      sku: string;
      quantity: string;
      unit_cost_pesos: string;
    }>;
  }>({
    supplier_id: "",
    warehouse_id: "",
    expected_delivery_date: "",
    notes: "",
    lines: [],
  });

  // Cuentas por pagar state
  const [payingInvoice, setPayingInvoice] = useState<PayableInvoicePublic | null>(null);
  const [paymentAmountPesos, setPaymentAmountPesos] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("transferencia");
  const [paymentReference, setPaymentReference] = useState("");

  // Queries
  const { data: suppliersData } = useQuery({
    queryKey: ["purchasing", "suppliers"],
    queryFn: () => listSuppliers(),
  });

  const { data: ordersData, isLoading: isLoadingOrders } = useQuery({
    queryKey: ["purchasing", "orders"],
    queryFn: () => listPurchaseOrders(),
    enabled: activeTab === "orders",
  });

  const { data: payablesData, isLoading: isLoadingPayables } = useQuery({
    queryKey: ["purchasing", "payables"],
    queryFn: () => listPayableInvoices(),
    enabled: activeTab === "payables",
  });

  const { data: warehousesData } = useQuery({
    queryKey: ["warehouses", branchId],
    queryFn: () => listWarehouses(branchId || undefined),
    enabled: Boolean(branchId),
  });

  const { data: productsData } = useQuery({
    queryKey: ["catalog", "products-for-po"],
    queryFn: () => listProducts({ limit: 100 }),
    enabled: showOrderModal,
  });

  // Mutations
  const createSupplierMutation = useMutation({
    mutationFn: (payload: SupplierCreate) => createSupplier(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "suppliers"] });
      setShowSupplierModal(false);
      resetSupplierForm();
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SupplierUpdate }) =>
      updateSupplier(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "suppliers"] });
      setEditingSupplier(null);
      resetSupplierForm();
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: (payload: PurchaseOrderCreate) => createPurchaseOrder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "orders"] });
      setShowOrderModal(false);
      resetOrderForm();
    },
  });

  const confirmOrderMutation = useMutation({
    mutationFn: (id: string) => confirmPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "orders"] });
    },
  });

  const receiveOrderMutation = useMutation({
    mutationFn: ({ id, invoiceNumber, notes }: { id: string; invoiceNumber: string; notes: string }) =>
      receivePurchaseOrder(id, {
        invoice_number: invoiceNumber || undefined,
        notes: notes || undefined,
        items: null, // Recibir todo lo pendiente
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "orders"] });
      queryClient.invalidateQueries({ queryKey: ["purchasing", "payables"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setReceivingOrderId(null);
      setReceiveInvoiceNumber("");
      setReceiveNotes("");
    },
  });

  const payInvoiceMutation = useMutation({
    mutationFn: ({ invoiceId, payload }: { invoiceId: string; payload: PayablePaymentCreate }) =>
      payPayableInvoice(invoiceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchasing", "payables"] });
      setPayingInvoice(null);
      setPaymentAmountPesos("");
      setPaymentReference("");
    },
  });

  function resetSupplierForm() {
    setSupplierForm({
      name: "",
      tax_id: "",
      contact_name: "",
      phone: "",
      email: "",
      address: "",
      credit_days: 0,
      notes: "",
    });
  }

  function resetOrderForm() {
    setOrderForm({
      supplier_id: "",
      warehouse_id: warehousesData?.[0]?.id ?? "",
      expected_delivery_date: "",
      notes: "",
      lines: [],
    });
  }

  function handleAddLine(variantId: string, sku: string, name: string) {
    if (!variantId) return;
    setOrderForm({
      ...orderForm,
      lines: [
        ...orderForm.lines,
        {
          variant_id: variantId,
          sku,
          name,
          quantity: "1",
          unit_cost_pesos: "0",
        },
      ],
    });
  }

  function handleRemoveLine(index: number) {
    setOrderForm({
      ...orderForm,
      lines: orderForm.lines.filter((_, idx) => idx !== index),
    });
  }

  function handleCreateOrderSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!orderForm.supplier_id || !orderForm.warehouse_id || orderForm.lines.length === 0) {
      alert("Selecciona proveedor, almacén y al menos una partida de compra.");
      return;
    }

    const payload: PurchaseOrderCreate = {
      branch_id: branchId,
      warehouse_id: orderForm.warehouse_id,
      supplier_id: orderForm.supplier_id,
      expected_delivery_date: orderForm.expected_delivery_date || undefined,
      notes: orderForm.notes || undefined,
      lines: orderForm.lines.map((l) => ({
        variant_id: l.variant_id,
        quantity: parseFloat(l.quantity) || 1,
        unit_cost_cents: Math.round(parseFloat(l.unit_cost_pesos || "0") * 100),
      })),
    };

    createOrderMutation.mutate(payload);
  }

  function handlePaySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!payingInvoice) return;
    const amountCents = Math.round(parseFloat(paymentAmountPesos || "0") * 100);
    if (amountCents <= 0) return;

    payInvoiceMutation.mutate({
      invoiceId: payingInvoice.id,
      payload: {
        amount_cents: amountCents,
        payment_method: paymentMethod,
        reference: paymentReference || undefined,
      },
    });
  }

  const supplierMap = new Map((suppliersData?.items ?? []).map((s) => [s.id, s.name]));

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface p-6">
      {/* Header y pestañas */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-carbon">
            Compras y Proveedores
          </h1>
          <p className="text-sm text-muted">
            Gestión de abastecimiento, entrada con costo promedio y cuentas por pagar.
          </p>
        </div>

        <div className="flex gap-2">
          {activeTab === "orders" && (
            <Button
              onClick={() => {
                resetOrderForm();
                setShowOrderModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nueva Orden de Compra
            </Button>
          )}
          {activeTab === "suppliers" && (
            <Button
              onClick={() => {
                resetSupplierForm();
                setShowSupplierModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Nuevo Proveedor
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex border-b border-line gap-4 text-sm font-semibold">
        <button
          onClick={() => setActiveTab("orders")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "orders"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Truck className="h-4 w-4" />
          Órdenes de Compra
        </button>
        <button
          onClick={() => setActiveTab("suppliers")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "suppliers"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Users className="h-4 w-4" />
          Directorio de Proveedores
        </button>
        <button
          onClick={() => setActiveTab("payables")}
          className={`flex items-center gap-2 border-b-2 pb-3 transition-colors ${
            activeTab === "payables"
              ? "border-electric text-electric-text font-bold"
              : "border-transparent text-muted hover:text-carbon"
          }`}
        >
          <Wallet className="h-4 w-4" />
          Cuentas por Pagar (Pasivos)
        </button>
      </div>

      {/* Tab: Órdenes de compra */}
      {activeTab === "orders" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">Folio</th>
                  <th className="p-4">Proveedor</th>
                  <th className="p-4">Emisión</th>
                  <th className="p-4">Entrega Esperada</th>
                  <th className="p-4">Total</th>
                  <th className="p-4">Estado</th>
                  <th className="p-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {isLoadingOrders ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted">
                      Cargando órdenes de compra...
                    </td>
                  </tr>
                ) : ordersData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted">
                      No hay órdenes de compra registradas.
                    </td>
                  </tr>
                ) : (
                  ordersData?.items.map((order) => {
                    const statusTone =
                      order.status === "recibida"
                        ? "ok"
                        : order.status === "confirmada"
                        ? "info"
                        : order.status === "borrador"
                        ? "warning"
                        : "danger";

                    return (
                      <tr key={order.id} className="hover:bg-surface/30">
                        <td className="p-4 font-mono font-bold text-carbon">
                          {order.series}-{order.folio}
                        </td>
                        <td className="p-4 font-medium text-carbon">
                          {supplierMap.get(order.supplier_id) ?? "Proveedor"}
                        </td>
                        <td className="p-4 text-xs text-muted">{order.issue_date}</td>
                        <td className="p-4 text-xs text-muted">
                          {order.expected_delivery_date ?? "Sin fecha"}
                        </td>
                        <td className="p-4 font-bold text-carbon">
                          <Money cents={order.total_cents} />
                        </td>
                        <td className="p-4">
                          <StatusPill tone={statusTone}>{order.status}</StatusPill>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {order.status === "borrador" && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => confirmOrderMutation.mutate(order.id)}
                                disabled={confirmOrderMutation.isPending}
                              >
                                Confirmar
                              </Button>
                            )}
                            {(order.status === "confirmada" || order.status === "borrador") && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setReceivingOrderId(order.id);
                                  setReceiveInvoiceNumber("");
                                  setReceiveNotes("");
                                }}
                                className="flex items-center gap-1.5"
                              >
                                <PackageCheck className="h-3.5 w-3.5" />
                                Recibir Mercancía
                              </Button>
                            )}
                            {order.status === "recibida" && (
                              <span className="text-xs text-success font-medium flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Ingresado al Inventario
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab: Directorio de Proveedores */}
      {activeTab === "suppliers" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">Proveedor</th>
                  <th className="p-4">RFC / NIF</th>
                  <th className="p-4">Contacto</th>
                  <th className="p-4">Teléfono / Email</th>
                  <th className="p-4">Días de Crédito</th>
                  <th className="p-4">Estado</th>
                  <th className="p-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {suppliersData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted">
                      No hay proveedores registrados.
                    </td>
                  </tr>
                ) : (
                  suppliersData?.items.map((sup) => (
                    <tr key={sup.id} className="hover:bg-surface/30">
                      <td className="p-4 font-semibold text-carbon">{sup.name}</td>
                      <td className="p-4 font-mono text-xs">{sup.tax_id ?? "Sin RFC"}</td>
                      <td className="p-4 text-xs">{sup.contact_name ?? "Sin contacto"}</td>
                      <td className="p-4 text-xs">
                        {sup.phone && <div>📞 {sup.phone}</div>}
                        {sup.email && <div className="text-muted">✉️ {sup.email}</div>}
                      </td>
                      <td className="p-4 text-xs font-medium">
                        {sup.credit_days > 0 ? `${sup.credit_days} días` : "Contado"}
                      </td>
                      <td className="p-4">
                        <StatusPill tone={sup.is_active ? "ok" : "neutral"}>
                          {sup.is_active ? "Activo" : "Inactivo"}
                        </StatusPill>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => {
                            setEditingSupplier(sup);
                            setSupplierForm({
                              name: sup.name,
                              tax_id: sup.tax_id ?? "",
                              contact_name: sup.contact_name ?? "",
                              phone: sup.phone ?? "",
                              email: sup.email ?? "",
                              address: sup.address ?? "",
                              credit_days: sup.credit_days,
                              notes: sup.notes ?? "",
                            });
                          }}
                          className="rounded p-1 text-muted hover:bg-surface hover:text-carbon"
                          title="Editar Proveedor"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab: Cuentas por Pagar */}
      {activeTab === "payables" && (
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface/50 text-xs font-semibold uppercase text-muted">
                  <th className="p-4">Factura Proveedor</th>
                  <th className="p-4">Proveedor</th>
                  <th className="p-4">Emisión</th>
                  <th className="p-4">Vencimiento</th>
                  <th className="p-4">Total</th>
                  <th className="p-4">Pagado</th>
                  <th className="p-4">Saldo Pendiente</th>
                  <th className="p-4">Estado</th>
                  <th className="p-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {isLoadingPayables ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted">
                      Cargando cuentas por pagar...
                    </td>
                  </tr>
                ) : payablesData?.items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted">
                      No hay cuentas por pagar pendientes.
                    </td>
                  </tr>
                ) : (
                  payablesData?.items.map((inv) => {
                    const statusTone =
                      inv.status === "pagada"
                        ? "ok"
                        : inv.status === "parcial"
                        ? "warning"
                        : "danger";

                    return (
                      <tr key={inv.id} className="hover:bg-surface/30">
                        <td className="p-4 font-mono font-bold text-carbon">
                          {inv.invoice_number}
                        </td>
                        <td className="p-4 font-medium text-carbon">
                          {supplierMap.get(inv.supplier_id) ?? "Proveedor"}
                        </td>
                        <td className="p-4 text-xs text-muted">{inv.issue_date}</td>
                        <td className="p-4 text-xs font-semibold text-danger">{inv.due_date}</td>
                        <td className="p-4">
                          <Money cents={inv.total_cents} />
                        </td>
                        <td className="p-4 text-success">
                          <Money cents={inv.paid_cents} />
                        </td>
                        <td className="p-4 font-bold text-danger">
                          <Money cents={inv.balance_cents} />
                        </td>
                        <td className="p-4">
                          <StatusPill tone={statusTone}>{inv.status}</StatusPill>
                        </td>
                        <td className="p-4 text-right">
                          {inv.status !== "pagada" && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setPayingInvoice(inv);
                                setPaymentAmountPesos(String(inv.balance_cents / 100));
                              }}
                              className="flex items-center gap-1.5"
                            >
                              <DollarSign className="h-3.5 w-3.5" />
                              Abonar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal Nueva Orden de Compra */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between border-b border-line pb-4">
              <h2 className="font-display text-lg font-bold text-carbon">
                Generar Orden de Compra
              </h2>
              <button onClick={() => setShowOrderModal(false)} className="text-muted hover:text-carbon">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOrderSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Proveedor *
                  </label>
                  <select
                    required
                    value={orderForm.supplier_id}
                    onChange={(e) => setOrderForm({ ...orderForm, supplier_id: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    <option value="">Selecciona proveedor...</option>
                    {suppliersData?.items.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Almacén Destino *
                  </label>
                  <select
                    required
                    value={orderForm.warehouse_id}
                    onChange={(e) => setOrderForm({ ...orderForm, warehouse_id: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  >
                    <option value="">Selecciona almacén...</option>
                    {warehousesData?.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Fecha Entrega Estimada
                  </label>
                  <input
                    type="date"
                    value={orderForm.expected_delivery_date}
                    onChange={(e) =>
                      setOrderForm({ ...orderForm, expected_delivery_date: e.target.value })
                    }
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              {/* Selector de producto para añadir partida */}
              <div className="rounded-msr border border-line bg-surface/50 p-4">
                <label className="mb-2 block text-xs font-bold uppercase text-carbon">
                  Agregar Producto a la Orden
                </label>
                <select
                  onChange={(e) => {
                    const variantId = e.target.value;
                    if (!variantId) return;
                    const prod = productsData?.items.find((p) =>
                      p.variants?.some((v) => v.id === variantId)
                    );
                    const variant = prod?.variants?.find((v) => v.id === variantId);
                    if (variant && prod) {
                      handleAddLine(variant.id, variant.sku, `${prod.name} - ${variant.name}`);
                    }
                    e.target.value = "";
                  }}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  defaultValue=""
                >
                  <option value="">Buscar o seleccionar del catálogo...</option>
                  {productsData?.items.map((p) =>
                    p.variants?.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.sku} - {p.name} ({v.name})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Partidas agregadas */}
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-muted">
                  Partidas de Compra ({orderForm.lines.length})
                </h3>
                {orderForm.lines.length === 0 ? (
                  <p className="text-xs text-muted">Aún no se han añadido partidas.</p>
                ) : (
                  <div className="space-y-2">
                    {orderForm.lines.map((line, idx) => (
                      <div
                        key={idx}
                        className="flex flex-wrap items-center gap-3 rounded-msr border border-line bg-snow p-3 text-sm"
                      >
                        <div className="min-w-[200px] flex-1">
                          <div className="font-semibold text-carbon">{line.name}</div>
                          <div className="font-mono text-xs text-muted">{line.sku}</div>
                        </div>

                        <div className="w-24">
                          <label className="block text-[10px] uppercase text-muted">Cantidad</label>
                          <input
                            type="number"
                            step="any"
                            min="0.0001"
                            required
                            value={line.quantity}
                            onChange={(e) => {
                              const updated = [...orderForm.lines];
                              if (updated[idx]) {
                                updated[idx].quantity = e.target.value;
                              }
                              setOrderForm({ ...orderForm, lines: updated });
                            }}
                            className="w-full rounded border border-line px-2 py-1 text-sm font-mono"
                          />
                        </div>

                        <div className="w-32">
                          <label className="block text-[10px] uppercase text-muted">
                            Costo Unit. (MXN)
                          </label>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            required
                            value={line.unit_cost_pesos}
                            onChange={(e) => {
                              const updated = [...orderForm.lines];
                              if (updated[idx]) {
                                updated[idx].unit_cost_pesos = e.target.value;
                              }
                              setOrderForm({ ...orderForm, lines: updated });
                            }}
                            className="w-full rounded border border-line px-2 py-1 text-sm font-mono"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveLine(idx)}
                          className="rounded p-1 text-danger hover:bg-danger-soft"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">Notas</label>
                <textarea
                  rows={2}
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  placeholder="Condiciones de entrega, folio de cotización del proveedor..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-line">
                <Button type="button" variant="secondary" onClick={() => setShowOrderModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={createOrderMutation.isPending}>
                  Crear Orden de Compra
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Recibir Mercancía */}
      {receivingOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <h2 className="mb-2 font-display text-lg font-bold text-carbon">
              Recepción de Mercancía en Almacén
            </h2>
            <p className="mb-4 text-xs text-muted">
              Al confirmar, el sistema ingresará las piezas al inventario, recalculará el costo
              promedio ponderado y creará la cuenta por pagar a crédito.
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Número de Factura / Remisión del Proveedor
                </label>
                <input
                  type="text"
                  placeholder="Ej. FAC-998822"
                  value={receiveInvoiceNumber}
                  onChange={(e) => setReceiveInvoiceNumber(e.target.value)}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Observaciones de Recepción
                </label>
                <textarea
                  rows={2}
                  placeholder="Mercancía en buen estado, sellos intactos..."
                  value={receiveNotes}
                  onChange={(e) => setReceiveNotes(e.target.value)}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-line">
                <Button
                  variant="secondary"
                  onClick={() => setReceivingOrderId(null)}
                  disabled={receiveOrderMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() =>
                    receiveOrderMutation.mutate({
                      id: receivingOrderId,
                      invoiceNumber: receiveInvoiceNumber,
                      notes: receiveNotes,
                    })
                  }
                  disabled={receiveOrderMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <PackageCheck className="h-4 w-4" />
                  Confirmar Entrada
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Pago / Abono a Proveedor */}
      {payingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <h2 className="mb-2 font-display text-lg font-bold text-carbon">
              Abonar a Factura {payingInvoice.invoice_number}
            </h2>
            <div className="mb-4 rounded-msr bg-surface p-3 text-xs">
              <div>Total: <Money cents={payingInvoice.total_cents} /></div>
              <div>Saldo Pendiente: <Money cents={payingInvoice.balance_cents} className="font-bold text-danger" /></div>
            </div>

            <form onSubmit={handlePaySubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Monto a Pagar (MXN) *
                </label>
                <input
                  type="number"
                  step="any"
                  min="0.01"
                  required
                  value={paymentAmountPesos}
                  onChange={(e) => setPaymentAmountPesos(e.target.value)}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm font-mono text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Método de Pago *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                >
                  <option value="transferencia">Transferencia SPEI</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="cheque">Cheque</option>
                  <option value="tarjeta">Tarjeta Débito/Crédito</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Referencia / Folio de Transferencia
                </label>
                <input
                  type="text"
                  placeholder="Ej. SPEI-8899223"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setPayingInvoice(null)}
                  disabled={payInvoiceMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={payInvoiceMutation.isPending}>
                  Registrar Pago
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Crear / Editar Proveedor */}
      {(showSupplierModal || editingSupplier) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-msr-lg border border-line bg-snow p-6 shadow-xl">
            <h2 className="mb-4 font-display text-lg font-bold text-carbon">
              {editingSupplier ? "Editar Proveedor" : "Nuevo Proveedor"}
            </h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (editingSupplier) {
                  updateSupplierMutation.mutate({
                    id: editingSupplier.id,
                    payload: supplierForm,
                  });
                } else {
                  createSupplierMutation.mutate(supplierForm);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Razón Social / Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  placeholder="Ej. Mayorista Tecnológico del Centro"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    RFC / Identificador Fiscal
                  </label>
                  <input
                    type="text"
                    value={supplierForm.tax_id ?? ""}
                    onChange={(e) =>
                      setSupplierForm({ ...supplierForm, tax_id: e.target.value.toUpperCase() })
                    }
                    className="w-full rounded-msr border border-line px-3 py-2 font-mono text-sm uppercase text-carbon focus:border-electric focus:outline-none"
                    placeholder="MTC123456ABC"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Contacto Comercial
                  </label>
                  <input
                    type="text"
                    value={supplierForm.contact_name ?? ""}
                    onChange={(e) =>
                      setSupplierForm({ ...supplierForm, contact_name: e.target.value })
                    }
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                    placeholder="Nombre del ejecutivo"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={supplierForm.phone ?? ""}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                    Días de Crédito
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={supplierForm.credit_days ?? 0}
                    onChange={(e) =>
                      setSupplierForm({
                        ...supplierForm,
                        credit_days: parseInt(e.target.value || "0", 10),
                      })
                    }
                    className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-muted">
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  value={supplierForm.email ?? ""}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  className="w-full rounded-msr border border-line px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-line">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowSupplierModal(false);
                    setEditingSupplier(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createSupplierMutation.isPending || updateSupplierMutation.isPending}
                >
                  {editingSupplier ? "Guardar Cambios" : "Crear Proveedor"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
